import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type ReactNode,
  type SetStateAction,
} from 'react';
import useEnvPermission from '../../hooks/useEnvPermission';
import useUserPreferences from '../../hooks/useUserPreferences';
import type { Report } from '../../types/types';
import type {
  HighlightedGraphNodeState,
  LineageColorBy,
  LineageDisplaySettings,
  LineageInfoPanelState,
  LineageNodeDegrees,
} from './types';
import { getLineageDisplaySettings } from './utils/lineageDisplaySettings';

export type LineageGraphContextValue = {
  highlightedNode: HighlightedGraphNodeState | undefined;
  setHighlightedNode: (node: HighlightedGraphNodeState | undefined) => void;
  exceededColumnLimitAssets: Set<string>;
  setExceededColumnLimitAssets: (ids: Set<string>) => void;
  lineageInfoPanel: LineageInfoPanelState;
  setLineageInfoPanel: (state: LineageInfoPanelState) => void;
  nodeDegrees: LineageNodeDegrees;
  setNodeDegrees: Dispatch<SetStateAction<LineageNodeDegrees>>;
  /** Column search text per expanded node, so it survives the card remounting. */
  columnSearchByNodeId: MutableRefObject<Record<string, string>>;
  /** Report JSON (lineage page); absent in controlled contexts (e.g. incident panel). */
  reportData?: Report;
  /** True when props override URL / tree selection (e.g. incident panel). */
  isControlled: boolean;
  columnFilterIds?: Set<string>;
  /** Saved per user on the lineage page; controlled contexts use the defaults plus `overrideColorBy`. */
  displaySettings: LineageDisplaySettings;
  updateDisplaySettings: (changes: Partial<LineageDisplaySettings>) => void;
  resetDisplaySettings: () => void;
};

const LineageGraphContext = createContext<LineageGraphContextValue | null>(
  null,
);

type ProviderProps = {
  children: ReactNode;
  /** Initial exceeded-column parent ids (e.g. from lineage API). */
  initialExceededColumnLimitAssetIds?: string[];
  reportData?: Report;
  isControlled?: boolean;
  columnFilterIds?: Set<string>;
  /** Controlled contexts only; replaces the default colorBy. */
  overrideColorBy?: LineageColorBy;
};

export const LineageGraphProvider = ({
  children,
  initialExceededColumnLimitAssetIds,
  reportData,
  isControlled = false,
  columnFilterIds,
  overrideColorBy,
}: ProviderProps) => {
  const [highlightedNode, setHighlightedNode] = useState<
    HighlightedGraphNodeState | undefined
  >(undefined);
  const [exceededColumnLimitAssets, setExceededColumnLimitAssets] = useState<
    Set<string>
  >(() => new Set(initialExceededColumnLimitAssetIds));
  const [lineageInfoPanel, setLineageInfoPanel] =
    useState<LineageInfoPanelState>({ isOpen: false });
  const [nodeDegrees, setNodeDegrees] = useState<LineageNodeDegrees>({
    byNodeId: {},
    max: 0,
  });
  const columnSearchByNodeId = useRef<Record<string, string>>({});
  const { userPreferences, updateUserPreferences, removeUserPreferences } =
    useUserPreferences();
  const hasPermission = useEnvPermission();
  const hasTestReadScope = hasPermission('VIEW_TESTS');

  const savedDisplaySettings = userPreferences.lineageDisplaySettings;
  const displaySettings = useMemo<LineageDisplaySettings>(
    () =>
      getLineageDisplaySettings(
        isControlled ? { colorBy: overrideColorBy } : savedDisplaySettings,
        hasTestReadScope,
      ),
    [isControlled, overrideColorBy, savedDisplaySettings, hasTestReadScope],
  );

  // Merges into the raw saved settings, not the resolved ones, so a value
  // hidden here (e.g. testStatus without VIEW_TESTS) survives for other envs.
  const updateDisplaySettings = useCallback(
    (changes: Partial<LineageDisplaySettings>) =>
      updateUserPreferences('lineageDisplaySettings', {
        ...savedDisplaySettings,
        ...changes,
      }),
    [savedDisplaySettings, updateUserPreferences],
  );

  // Clears rather than saving the defaults, so later default changes apply.
  const resetDisplaySettings = useCallback(
    () => removeUserPreferences('lineageDisplaySettings'),
    [removeUserPreferences],
  );

  const serializedInitial = JSON.stringify(
    initialExceededColumnLimitAssetIds ?? [],
  );
  useEffect(() => {
    setExceededColumnLimitAssets(
      new Set(JSON.parse(serializedInitial) as string[]),
    );
  }, [serializedInitial]);

  const value = useMemo(
    () => ({
      highlightedNode,
      setHighlightedNode,
      exceededColumnLimitAssets,
      setExceededColumnLimitAssets,
      lineageInfoPanel,
      setLineageInfoPanel,
      nodeDegrees,
      setNodeDegrees,
      columnSearchByNodeId,
      reportData,
      isControlled,
      columnFilterIds,
      displaySettings,
      updateDisplaySettings,
      resetDisplaySettings,
    }),
    [
      highlightedNode,
      exceededColumnLimitAssets,
      lineageInfoPanel,
      nodeDegrees,
      reportData,
      isControlled,
      columnFilterIds,
      displaySettings,
      updateDisplaySettings,
      resetDisplaySettings,
    ],
  );

  return (
    <LineageGraphContext.Provider value={value}>
      {children}
    </LineageGraphContext.Provider>
  );
};

export const useLineageGraphContext = (): LineageGraphContextValue => {
  const ctx = useContext(LineageGraphContext);
  if (!ctx) {
    throw new Error(
      'useLineageGraphContext must be used within LineageGraphProvider',
    );
  }
  return ctx;
};
